import React, { useEffect, useState } from 'react';
import { makeStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Typography from 'Components/UI/Typography';
import useUserContext from 'Context/UserContext';
import useCollection from 'Hooks/useCollection';
import fr from 'facet-rest';
import { displayErrorMessage, displaySuccess } from 'Helpers/FacetNotificationManager';
import OutgoingMessageCard, { MessageCardSkeleton as OutgoingMessageCardSkeleton } from 'Components/Modals/BulkCommunicationDialog/components/MessageCard';
import {
  addQuery, removeQuery,
} from 'Helpers/Query';
import { noop } from 'Helpers/PropTypes';
import { truncate } from 'lodash';
import moment from 'moment';
import { stripHTML } from 'Helpers/Utils';

const useStyles = makeStyles((theme) => ({
  root: {
  },
  cardContainer: {
    maxHeight: '600px',
    overflowY: 'scroll',
  },
  title: {
    marginBottom: theme.spacing(3),
    fontSize: '16px',
  },
  messagesCard: {
    marginBottom: theme.spacing(2),
  },
}));

const MyOutgoingMessagesLoadingIndicator = () => {
  const classes = useStyles();
  return (
    <div className={classes.root} test={'recommended-bulk-communication-my-messages-skeleton'}>
      <Typography
        className={classes.title}
        color={'primary'}
        variant={'h6'}
      >
        Outgoing Messages
      </Typography>
      <OutgoingMessageCardSkeleton className={classes.messagesCard} />
    </div>
  );
};

const MyOutgoingMessages = () => {
  const classes = useStyles();
  const { collection, setCollection } = useCollection();

  const { agentResource } = useUserContext();
  const [loading, setLoading] = useState();

  const loadOutgoingMessages = async () => {
    if (!agentResource?.relationship(fr.relationships.outgoingBulkCommunications.name)) return;
    try {
      setLoading(true);
      const coll = await agentResource?.relationship(fr.relationships.outgoingBulkCommunications.name).link('related').get();
      setCollection(coll);
    } catch (err) {
      displayErrorMessage(err, err.errors);
    } finally {
      setLoading(false);
    }
  };

  const onDelete = async (outgoingBulkCommMessage) => {
    await outgoingBulkCommMessage.link('self').delete()
      .then(() => displaySuccess('Completed Outgoing Bulk Message cleared.', 1500))
      .catch(() => displayErrorMessage('Failed to clear Outgoing Bulk Message.', 1500));
    await loadOutgoingMessages();
  };

  useEffect(() => {
    loadOutgoingMessages();
  }, []);

  if (loading) {
    return <MyOutgoingMessagesLoadingIndicator />;
  }

  if (!collection) {
    return null;
  }

  const canDelete = outgoingBulkCommMessage => outgoingBulkCommMessage.meta().canDelete();

  return (
    <div className={classes.root} test={'bulk-communication-outgoing-messages-container'}>
      <Typography
        className={classes.title}
        color={'primary'}
        variant={'h6'}
      >
        {'Outgoing Messages'}
      </Typography>
      <div className={classes.cardContainer}>
        {collection.resources().length() > 0 ? (
          collection.resources().list().map(outgoingBulkCommMessage => {
            const outgoingMessageTitle = `${moment(outgoingBulkCommMessage.attr('sendAt')).format('M/DD/YYYY h:mma')}`;

            return (
              <OutgoingMessageCard
                className={classes.messagesCard}
                test={'bulk-communication-outgoing-message-card'}
                key={outgoingBulkCommMessage.link('self').url()}
                title={outgoingMessageTitle}
                sendAt={outgoingBulkCommMessage.attr('sendAt')}
                onClick={() => {
                  if (outgoingBulkCommMessage?.relationship(fr.relationships.bulkCommunicationPreview.name)) {
                    removeQuery('bulkCommunicationLink');
                    addQuery({
                      bulkCommunicationLink: outgoingBulkCommMessage
                        ?.relationship(fr.relationships.bulkCommunicationPreview.name)
                        .link('related')
                        .url(),
                    });
                  } else {
                    addQuery({ modal: 'bulkCommunicationSummary' }, {
                      bulkCommunicationSummaryLink: outgoingBulkCommMessage
                        ?.relationship(fr.relationships.bulkCommunicationSummary.name)
                        .link('related')
                        .url(),
                    });
                  }
                }}
                closeButton={canDelete(outgoingBulkCommMessage)}
                onClose={() => {
                  if (canDelete(outgoingBulkCommMessage)) {
                    return onDelete(outgoingBulkCommMessage);
                  }
                  return noop();
                }}
                subject={
                  truncate(outgoingBulkCommMessage.attr('subject'), {
                    length: 30,
                    omission: '...',
                  })
                }
                content={
                  truncate(stripHTML(outgoingBulkCommMessage.attr('content')), {
                    length: 30,
                    omission: '...',
                  })
                }
              >
                {outgoingBulkCommMessage.attr('isDraft') === true ? (
                  <Typography
                    className={classes.title}
                    color={'primary'}
                    variant={'h6'}
                  >
                    {'Draft'}
                  </Typography>) : null
                }
              </OutgoingMessageCard>
            );
          })) : <Typography>You don&apos;t have any outgoing messages</Typography>
        }
      </div>
    </div>
  );
};

MyOutgoingMessages.defaultProps = {};
MyOutgoingMessages.propTypes = {};

export default MyOutgoingMessages;
